<template>
  <BaseModal
    :heading="$t('customers.tags.heading')"
    small
    useForm
    @submit="submit"
    @close="$emit('close')"
  >
    <Flex
      direction="column"
      :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]"
    >
      <BaseText>{{ $t('customers.tags.description') }}</BaseText>
      <BaseText
        :href="helpCenterArticleUrl($t('customers.tags.help_article_url'))"
      >
        {{ $t('global.learn_more') }}
      </BaseText>
      <Flex
        fullWidth
        wrap
      >
        <div
          v-for="(tag, index) in tagsClone"
          :key="index"
          :class="[$style.card, { [$style.smallScreen]: $screen === 's' }]"
        >
          <BaseCard
            :closable="!tag.salonized"
            @close="removeTag(index)"
            gray
          >
            <BaseInput
              v-model="tag.name"
              :disabled="tag.salonized"
              :label="$t('customers.tags.tag_name')"
              :info="tag.salonized ? $t('customers.tags.undeletable') : undefined"
              v-test="'tag-input'"
            />
          </BaseCard>
        </div>
      </Flex>
      <BaseText
        v-if="tagsClone.length < 6"
        iconBefore="plus"
        @click="addTag"
        v-test="'tag-add'"
      >
        {{ $t('customers.tags.create_tag') }}
      </BaseText>
    </Flex>
    <template #footer>
      <BaseButton
        submitForm
        :loading="isLoading"
        v-test="'save-tags'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { flash, modal } from '@/helpers/ui';

const { t } = useI18n();

type Tag = {
  id: number | null;
  name: string;
  salonized?: boolean;
};

const props = defineProps<{
  tags: Tag[];
}>();

const tagsClone = ref<Tag[]>(JSON.parse(JSON.stringify(props.tags)));

const addTag = () => {
  tagsClone.value.push({
    id: null,
    name: '',
  });
};

const removeTag = (index: number) => {
  tagsClone.value = tagsClone.value.filter((_, tagIndex) => tagIndex !== index);
};

const emit = defineEmits(['save', 'close']);
const isLoading = ref(false);

const submit = () => {
  modal('confirmation', {
    warning: t('customers.tags.description_warning'),
    type: 'confirmation',
  }).then(() => {
    isLoading.value = true;
    const { mutate } = useMutation(gql`
      mutation updateCustomerTags($input: UpdateCustomerTagsInput!) {
        updateCustomerTags(input: $input) {
          customerTags {
            id
          }
        }
      }
    `);
    mutate({
      input: {
        customerTags: tagsClone.value
          .filter((tag) => tag.name && !tag.salonized)
          .map((tag) => ({
            id: tag.id,
            name: tag.name,
          })),
      },
    }).then(() => {
      isLoading.value = false;
      flash(t('global.flash.label_updated'));
      emit('save');
    });
  });
};
</script>

<style
  lang="scss"
  module
>
.card {
  width: calc(50% - #{$spacing * 0.5});

  &.smallScreen {
    width: 100%;
  }
}
</style>
